import { useCallback, useEffect, useRef, useState } from 'react';
import { useAccountChanges, type AccountChangeEvent } from '@/modules/mcpStream';

const PROMPT_WAIT_MS = 1000;

// Every editor save arrives as a scene update, but a card shows only status and cover, so those catch up at this pace
const CONTENT_UPDATE_WAIT_MS = 30_000;

const isContentUpdate = (event: AccountChangeEvent) =>
  event.op === 'updated' && (event.entity === 'scene' || event.entity === 'story');

export function useProjectsChangeVersion(): number {
  const [version, setVersion] = useState(0);
  const pendingWhileHidden = useRef(false);
  const slowTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const bump = useCallback(() => {
    clearTimeout(slowTimer.current);
    slowTimer.current = undefined;
    if (document.hidden) {
      pendingWhileHidden.current = true;
      return;
    }
    setVersion(n => n + 1);
  }, []);

  useAccountChanges(['project', 'story', 'scene'], events => {
    // An empty batch means the stream reopened and changes may have been missed
    if (events.length === 0 || events.some(event => !isContentUpdate(event))) {
      bump();
      return;
    }
    slowTimer.current ??= setTimeout(bump, CONTENT_UPDATE_WAIT_MS);
  }, PROMPT_WAIT_MS);

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden || !pendingWhileHidden.current) return;
      pendingWhileHidden.current = false;
      bump();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(slowTimer.current);
    };
  }, [bump]);

  return version;
}
